import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useFooterToggle } from '@hooks/footerDashboardToggle';
import { NavLink, useLocation } from 'react-router-dom';
import { IFooterTabType } from '.';

const FOOTER_TABS: Omit<IFooterTabType, 'active'>[] = [
	{
		key: 'dashboard',
		path: '/',
		label: 'Dashboard',
		icon: 'icon-dashboard-menu',
		iconFilled: 'icon-dashboard-menu',
		isButton: false,
	},
	{
		key: 'end-users',
		path: ['/end-users', '/leads'],
		label: 'Users',
		icon: 'icon-users',
		iconFilled: 'icon-users',
		isButton: false,
	},
	{
		key: 'sites',
		path: '/prospective-site',
		label: 'Sites',
		icon: 'icon-mdp',
		iconFilled: 'icon-mdp',
		isButton: false,
	},
	{
		key: 'meets',
		path: ['/meetings', '/meetings/todays-meetings', '/meetings/all-meetings'],
		label: 'Meets',
		icon: 'icon-calendar',
		iconFilled: 'icon-calendar',
		isButton: false,
	},
];

export const Footer = (): ReactElement => {
	const { pathname } = useLocation();
	const { toggleTab } = useFooterToggle();

	useEffect(() => {
		toggleTab();
		window.addEventListener('resize', toggleTab);
		return (): void => window.removeEventListener('resize', toggleTab);
	}, [pathname, toggleTab]);

	const isActivePath = useCallback(
		(targets: string | string[]): boolean => {
			if (Array.isArray(targets)) {
				return targets.some((path) =>
					path.endsWith('*') ? pathname.startsWith(path.replace('*', '')) : pathname === path
				);
			}
			return targets.endsWith('*') ? pathname.startsWith(targets.replace('*', '')) : pathname === targets;
		},
		[pathname]
	);

	const tabs: IFooterTabType[] = useMemo(
		() =>
			FOOTER_TABS.map((tab) => ({
				...tab,
				active: isActivePath(tab.path),
			})),
		[isActivePath]
	);

	return (
		<footer className="footer">
			<div className="footer__wrapper">
				<div className="footer__container">
					<ul className="footer__tabs">
						{tabs.map((tab) => (
							<li key={tab.key} className={`footer__tab ${tab.active ? 'footer__tab--active' : ''}`}>
								{tab.isButton ? (
									<button type="button" className="footer__tab-link" onClick={tab.onClick} aria-label={tab.label}>
										<span className={`${tab.active ? (tab.iconFilled ?? tab.icon) : tab.icon} footer__tab-icon`} />
										<span className="footer__tab-label">{tab.label}</span>
									</button>
								) : (
									<NavLink
										to={Array.isArray(tab.path) ? tab.path[0] : tab.path}
										className="footer__tab-link"
										aria-label={tab.label}
									>
										<span className={`${tab.active ? (tab.iconFilled ?? tab.icon) : tab.icon} footer__tab-icon`} />
										<span className="footer__tab-label">{tab.label}</span>
									</NavLink>
								)}
							</li>
						))}
					</ul>
				</div>
			</div>
		</footer>
	);
};
